import * as MUI from "@mui/material";
import React from "react";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ShoppingBagOutlined from "@mui/icons-material/ShoppingBagOutlined";
import { useTranslation } from "react-i18next";
import { useWarehouseLabelBuffer } from "../../../../../../contexts/WarehouseLabelBuffer";
import { useWarehouseShoppingListBuffer } from "../../../../../../contexts/WarehouseShoppingListBuffer";
import { tableHeadRowSx } from "../../../../../../enums/Styles";
import { trimText } from "../../../../../../utils/Functions";
import { formatCurrency, getStockStatus } from "../utils/stockStatus";
import QuantityForm from "../form/QuantityForm";

const columns = [
  { id: "index", label: "#", align: "left", width: 56, sortable: false },
  { id: "title", label: "Title", align: "left", sortable: true },
  { id: "description", label: "Description", align: "left", sortable: true },
  { id: "code", label: "Code", align: "left", sortable: true },
  { id: "category", label: "Category", align: "left", sortable: true },
  { id: "supplier", label: "Supplier", align: "left", sortable: true },
  { id: "price", label: "Price", align: "right", sortable: true },
  { id: "pdv", label: "PDV", align: "right", sortable: true },
  { id: "priceWithTax", label: "PriceWithVat", align: "right", sortable: true },
  { id: "quantity", label: "Quantity", align: "right", sortable: true },
  { id: "status", label: "Status", align: "left", sortable: false },
  { id: "labelBuffer", label: "", align: "center", width: 56, sortable: false },
  { id: "shoppingList", label: "", align: "center", width: 56, sortable: false },
];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function WarehouseTable({
  items,
  currency,
  onRowClick,
  order,
  orderBy,
  onSort,
  isLoading,
  emptyMessage,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) {
  const { t } = useTranslation();
  const [quantityAnchorEl, setQuantityAnchorEl] = React.useState(null);
  const [quantityItem, setQuantityItem] = React.useState(null);
  const { isBuffered, toggle } = useWarehouseLabelBuffer();
  const { isBuffered: isInShoppingList, toggle: toggleShoppingList } = useWarehouseShoppingListBuffer();

  function handleBufferToggle(e, item) {
    e.stopPropagation();
    toggle(item);
  }

  function handleShoppingListToggle(e, item) {
    e.stopPropagation();
    toggleShoppingList(item);
  }

  function handleSort(columnId) {
    if (!onSort) return;
    const isAsc = orderBy === columnId && order === "asc";
    onSort(columnId, isAsc ? "desc" : "asc");
  }

  function handleQuantityClick(e, item) {
    e.stopPropagation();
    setQuantityAnchorEl(e.currentTarget);
    setQuantityItem(item);
  }

  function handleQuantityClose() {
    setQuantityAnchorEl(null);
    setQuantityItem(null);
  }

  const isQuantityPopoverOpen = Boolean(quantityAnchorEl);

  return (
    <MUI.Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "divider",
        overflow: "hidden",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <MUI.TableContainer sx={{ overflowX: "auto", minWidth: 0 }}>
        <MUI.Table sx={{ minWidth: 1200 }}>
          <MUI.TableHead>
            <MUI.TableRow sx={tableHeadRowSx}>
              {columns?.map((column) => (
                <MUI.TableCell
                  key={column.id}
                  align={column.align}
                  sx={{ width: column.width }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {column.id === "index" ? (
                    ""
                  ) : column.id === "labelBuffer" ? (
                    <MUI.Tooltip title={t("AddToLabelQueue")}>
                      <QrCode2Icon fontSize="small" />
                    </MUI.Tooltip>
                  ) : column.id === "shoppingList" ? (
                    <MUI.Tooltip title={t("AddToShoppingList")}>
                      <ShoppingBagOutlined fontSize="small" />
                    </MUI.Tooltip>
                  ) : column.sortable ? (
                    <MUI.TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleSort(column.id)}
                      data-testid={`warehouse.items.sort.${column.id}`}
                    >
                      {t(column.label)}
                    </MUI.TableSortLabel>
                  ) : (
                    t(column.label)
                  )}
                </MUI.TableCell>
              ))}
            </MUI.TableRow>
          </MUI.TableHead>
          <MUI.TableBody>
            {isLoading ? (
              <MUI.TableRow>
                <MUI.TableCell
                  colSpan={columns.length}
                  sx={{ border: 0, py: 6 }}
                >
                  <MUI.Stack alignItems="center" justifyContent="center">
                    <MUI.CircularProgress size={28} />
                  </MUI.Stack>
                </MUI.TableCell>
              </MUI.TableRow>
            ) : items.length === 0 ? (
              <MUI.TableRow>
                <MUI.TableCell
                  colSpan={columns.length}
                  sx={{ border: 0, py: 3 }}
                >
                  <MUI.Typography
                    variant="body1"
                    color={"grayText"}
                    data-testid="warehouse.items.empty-message"
                  >
                    {t(emptyMessage) ?? t("NoWarehouseItems")}
                  </MUI.Typography>
                </MUI.TableCell>
              </MUI.TableRow>
            ) : (
              items.map((item, index) => {
                const status = getStockStatus(item.quantity);
                return (
                  <MUI.TableRow
                    hover
                    key={item.id}
                    onClick={() => onRowClick(item)}
                    sx={{
                      cursor: "pointer",
                      "&:last-child td": { borderBottom: 0 },
                    }}
                    data-testid={`warehouse.items.row.${item.id}`}
                  >
                    <MUI.TableCell
                      sx={{ color: "text.disabled" }}
                      data-testid={`warehouse.items.row.${item.id}.index`}
                    >
                      {page * rowsPerPage + index + 1}
                    </MUI.TableCell>
                    <MUI.TableCell>
                      <MUI.Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                      >
                        <MUI.Tooltip title={item.title}>
                          <MUI.Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                            data-testid={`warehouse.items.row.${item.id}.title`}
                          >
                            {trimText(item.title, 30)}
                          </MUI.Typography>
                        </MUI.Tooltip>
                      </MUI.Stack>
                    </MUI.TableCell>
                    <MUI.TableCell>
                      {item.description ? (
                        <MUI.Tooltip title={item.description}>
                          <MUI.Typography
                            variant="body2"
                            color="text.secondary"
                            data-testid={`warehouse.items.row.${item.id}.description`}
                          >
                            {trimText(item.description, 30)}
                          </MUI.Typography>
                        </MUI.Tooltip>
                      ) : (
                        <MUI.Typography
                          variant="body2"
                          color="text.disabled"
                          data-testid={`warehouse.items.row.${item.id}.description`}
                        >
                          N/A
                        </MUI.Typography>
                      )}
                    </MUI.TableCell>
                    <MUI.TableCell>
                      {item.code ? (
                        <MUI.Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontFamily: "monospace" }}
                          data-testid={`warehouse.items.row.${item.id}.code`}
                        >
                          {item.code}
                        </MUI.Typography>
                      ) : (
                        <MUI.Typography
                          variant="body2"
                          color="text.disabled"
                          data-testid={`warehouse.items.row.${item.id}.code`}
                        >
                          N/A
                        </MUI.Typography>
                      )}
                    </MUI.TableCell>
                    <MUI.TableCell>
                      {item.category?.name ? (
                        <MUI.Tooltip title={item.category?.name}>
                          <MUI.Chip
                            label={trimText(item.category?.name, 30)}
                            size="small"
                            variant="filled"
                            data-testid={`warehouse.items.row.${item.id}.category`}
                          />
                        </MUI.Tooltip>
                      ) : (
                        <MUI.Typography
                          variant="body2"
                          color="text.disabled"
                          data-testid={`warehouse.items.row.${item.id}.category`}
                        >
                          N/A
                        </MUI.Typography>
                      )}
                    </MUI.TableCell>
                    <MUI.TableCell>
                      {item.supplier?.name ? (
                        <MUI.Tooltip title={item.supplier?.name}>
                          <MUI.Typography
                            variant="body2"
                            color="text.secondary"
                            data-testid={`warehouse.items.row.${item.id}.supplier`}
                          >
                            {trimText(item.supplier?.name, 30)}
                          </MUI.Typography>
                        </MUI.Tooltip>
                      ) : (
                        <MUI.Typography
                          variant="body2"
                          color="text.disabled"
                          data-testid={`warehouse.items.row.${item.id}.supplier`}
                        >
                          N/A
                        </MUI.Typography>
                      )}
                    </MUI.TableCell>
                    <MUI.TableCell align="right">
                      <MUI.Typography
                        variant="body2"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                        data-testid={`warehouse.items.row.${item.id}.price`}
                      >
                        {formatCurrency(item.price, currency)}
                      </MUI.Typography>
                    </MUI.TableCell>
                    <MUI.TableCell align="right">
                      <MUI.Typography
                        variant="body2"
                        color="text.secondary"
                        data-testid={`warehouse.items.row.${item.id}.pdv`}
                      >
                        {Number(item.pdv)}%
                      </MUI.Typography>
                    </MUI.TableCell>
                    <MUI.TableCell align="right">
                      <MUI.Typography
                        variant="body2"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          fontWeight: 600,
                        }}
                        data-testid={`warehouse.items.row.${item.id}.price-with-vat`}
                      >
                        {formatCurrency(item.priceWithTax, currency)}
                      </MUI.Typography>
                    </MUI.TableCell>
                    <MUI.TableCell align="right">
                      <MUI.Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        alignItems="center"
                        onClick={(e) => handleQuantityClick(e, item)}
                        sx={{
                          cursor: "pointer",
                          display: "inline-flex",
                          ml: "auto",
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: (theme) =>
                              theme.palette.mode === "light"
                                ? "grey.100"
                                : "rgba(255,255,255,0.08)",
                          },
                        }}
                      >
                        <MUI.Typography
                          marginLeft={8}
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          data-testid={`warehouse.items.row.${item.id}.quantity`}
                        >
                          {item.quantity}
                        </MUI.Typography>
                      </MUI.Stack>
                    </MUI.TableCell>
                    <MUI.TableCell>
                      <MUI.Chip
                        label={t(status.label)}
                        size="small"
                        color={status.color}
                        variant="filled"
                        data-testid={`warehouse.items.row.${item.id}.status-chip`}
                      />
                    </MUI.TableCell>
                    <MUI.TableCell align="center" padding="checkbox">
                      <MUI.Checkbox
                        checked={isBuffered(item.id)}
                        onClick={(e) => handleBufferToggle(e, item)}
                        title={t("AddToLabelQueue")}
                        size="small"
                        inputProps={{
                          "data-testid": `warehouse.items.row.${item.id}.label-queue-checkbox`,
                        }}
                      />
                    </MUI.TableCell>
                    <MUI.TableCell align="center" padding="checkbox">
                      <MUI.Checkbox
                        checked={isInShoppingList(item.id)}
                        onClick={(e) => handleShoppingListToggle(e, item)}
                        title={t("AddToShoppingList")}
                        size="small"
                        inputProps={{
                          "data-testid": `warehouse.items.row.${item.id}.shopping-list-checkbox`,
                        }}
                      />
                    </MUI.TableCell>
                  </MUI.TableRow>
                );
              })
            )}
          </MUI.TableBody>
        </MUI.Table>
      </MUI.TableContainer>

      <MUI.TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        labelRowsPerPage={t("RowsPerPage")}
        onPageChange={(e, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) => onRowsPerPageChange(Number(e.target.value))}
        backIconButtonProps={{
          title: t("PreviousPage"),
          "aria-label": t("PreviousPage"),
        }}
        nextIconButtonProps={{
          title: t("NextPage"),
          "aria-label": t("NextPage"),
        }}
      />

      <MUI.Popover
        open={isQuantityPopoverOpen}
        anchorEl={quantityAnchorEl}
        onClose={handleQuantityClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MUI.Box sx={{ p: 2, width: 450 }} data-testid="warehouse.items.quantity-popover">
          <MUI.Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {quantityItem?.title ? `${trimText(quantityItem.title, 24)}` : ""}
          </MUI.Typography>

          <MUI.Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ mb: 2 }}
          >
            <QuantityForm
              quantity={quantityItem?.quantity}
              item={quantityItem}
              handleQuantityClose={handleQuantityClose}
            />
          </MUI.Stack>
        </MUI.Box>
      </MUI.Popover>
    </MUI.Paper>
  );
}
